/**
 * Login page - Clean, minimal, modern design
 * IMPORTANT: This page is always rendered in light mode for consistency
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/user';
import PageTransition from '../components/common/PageTransition';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  ThemeProvider,
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import { createAppTheme } from '../theme/theme';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  // Force light mode for login page to ensure consistent appearance
  const lightTheme = createAppTheme('light');

  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setInfoMessage(state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);

      // Get user data from stored user object
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userRole = user?.role;
      const mustChangePassword = user?.must_change_password;

      // Check if password change is required
      if (mustChangePassword === true) {
        // For employees, redirect to employee profile; for others, redirect to admin profile
        if (userRole === UserRole.EMPLOYEE) {
          navigate('/employee/profile?tab=password&required=true');
        } else {
          navigate('/profile?tab=password&required=true');
        }
      } else {
        // Role-based redirect to dashboard
        if (userRole === UserRole.EMPLOYEE) {
          navigate('/employee/dashboard');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <PageTransition>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            // Clean white background with subtle warmth
            background: `
              linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 25%, #F5F5F5 50%, #FAFAFA 75%, #FFFFFF 100%)
            `,
            overflow: 'hidden',
          }}
        >
        {/* Large Grid Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(249, 115, 22, 0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(249, 115, 22, 0.06) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: '-25%',
            right: '-15%',
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(249, 115, 22, 0.12) 0%, rgba(251, 146, 60, 0.04) 35%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Bottom Left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            left: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(234, 88, 12, 0.08) 0%, rgba(249, 115, 22, 0.03) 40%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(70px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Center (behind card) */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, rgba(251, 146, 60, 0.04) 40%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Top Left (subtle) */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '-5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(251, 146, 60, 0.06) 0%, transparent 60%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            zIndex: 0,
          }}
        />

        {/* Accent Line - Top */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, transparent 0%, #F97316 50%, transparent 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/Datafact Z New Logo Dark.png"
              alt="DataFactz"
              sx={{
                height: '40px',
                mb: 6,
              }}
            />

            {/* Login Card - Glassmorphic */}
            <Card
              sx={{
                width: '100%',
                maxWidth: 360,
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  component="h1"
                  align="center"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 3,
                    color: '#1D1D1F',
                    letterSpacing: '-0.02em',
                    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                  }}
                >
                  Welcome back
                </Typography>

                {infoMessage && (
                  <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: '12px' }}
                    onClose={() => setInfoMessage('')}
                  >
                    {infoMessage}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    required
                    fullWidth
                    id="username"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    placeholder="Username"
                    sx={{
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        fontSize: '0.938rem',
                        height: '48px',
                        color: '#1D1D1F',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(249, 115, 22, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#F97316',
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '12px 16px',
                        '&::placeholder': {
                          color: '#6B7280',
                          opacity: 1,
                        },
                      },
                    }}
                  />

                  <TextField
                    required
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    placeholder="Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: '#9CA3AF',
                              mr: 0.5,
                              '&:hover': {
                                color: '#F97316',
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            {showPassword ? (
                              <EyeOff size={20} strokeWidth={1.5} />
                            ) : (
                              <Eye size={20} strokeWidth={1.5} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        fontSize: '0.938rem',
                        height: '48px',
                        color: '#1D1D1F',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(249, 115, 22, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#F97316',
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '12px 16px',
                        '&::placeholder': {
                          color: '#6B7280',
                          opacity: 1,
                        },
                      },
                    }}
                  />

                  {/* Forgot Password */}
                  <Box sx={{ mb: 2.5, textAlign: 'right' }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => setForgotPasswordOpen(true)}
                      sx={{
                        fontSize: '0.813rem',
                        color: '#F97316',
                        textDecoration: 'none',
                        fontWeight: 500,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        '&:hover': {
                          color: '#EA580C',
                        },
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      height: '42px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '10px',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      background: 'linear-gradient(135deg, #FB923C 0%, #F97316 100%)',
                      boxShadow: '0 2px 8px -2px rgba(249, 115, 22, 0.3)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                        boxShadow: '0 4px 12px -2px rgba(249, 115, 22, 0.4)',
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                      '&.Mui-disabled': {
                        background: 'linear-gradient(135deg, #FDBA74 0%, #FB923C 100%)',
                        color: 'rgba(255, 255, 255, 0.8)',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          open={forgotPasswordOpen}
          onClose={() => setForgotPasswordOpen(false)}
        />
      </Box>
      </PageTransition>
    </ThemeProvider>
  );
};

export default Login;
